import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import { articles, getArticleById, getCategoryName, siteName, siteUrl } from '@/mocks/melons';

export default function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  const related = useMemo(() => {
    if (!article) return [];
    const pool = articles.filter((a) => a.id !== article.id && (a.categoryId === article.categoryId || a.series === article.series));
    return (pool.length ? pool : articles.filter((a) => a.id !== article.id)).slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="container-page flex flex-col items-center py-28 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
          <i className="ri-error-warning-line text-3xl" aria-hidden="true" />
        </span>
        <h1 className="mt-5 text-2xl font-bold text-foreground-950">没有找到这颗瓜</h1>
        <p className="mt-2 text-sm text-foreground-600">它可能已下线或被重新整理，回到首页热榜看看大家在聊什么吧。</p>
        <a href="/" className="mt-6 rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-700">
          返回首页
        </a>
      </div>
    );
  }

  const canonical = `${siteUrl}/post/${article.id}`;
  const catName = getCategoryName(article.categoryId);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: catName, item: `${siteUrl}/#featured` },
        { '@type': 'ListItem', position: 3, name: article.title, item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      image: article.image,
      author: { '@type': 'Organization', name: siteName, url: siteUrl },
      publisher: { '@type': 'Organization', name: siteName, url: siteUrl },
      datePublished: '2026-09-08',
      dateModified: '2026-09-09',
      mainEntityOfPage: canonical,
      keywords: `快手吃瓜,${article.categoryName},${article.tagLabel}`,
    },
  ];

  return (
    <>
      <Seo title={`${article.title} - 快手吃瓜`} description={article.summary} keywords={`快手吃瓜,${article.categoryName},${article.tagLabel}`} canonicalUrl={canonical} type="article" jsonLd={jsonLd} />

      <nav className="border-b border-background-200 bg-background-50" aria-label="面包屑">
        <div className="container-page flex items-center gap-2 overflow-x-auto py-3 text-[13px] text-foreground-500 scroll-hidden">
          <a href="/" className="inline-flex items-center gap-1 whitespace-nowrap hover:text-primary-700"><i className="ri-home-4-line" aria-hidden="true" />首页</a>
          <i className="ri-arrow-right-s-line shrink-0" aria-hidden="true" />
          <a href="/#featured" className="whitespace-nowrap hover:text-primary-700">{catName}</a>
          <i className="ri-arrow-right-s-line shrink-0" aria-hidden="true" />
          <span className="line-clamp-1 min-w-0 text-foreground-700">{article.title}</span>
        </div>
      </nav>

      <div className="container-page grid grid-cols-1 gap-8 py-10 md:py-14 lg:grid-cols-12">
        <article className="lg:col-span-8">
          <header>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-600 px-2.5 py-1 text-xs font-bold text-background-50">{article.tagLabel}</span>
              <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-semibold text-foreground-600">{article.categoryName}</span>
              <span className="text-xs text-foreground-500">更新于 {article.time}</span>
            </div>
            <h1 className="mt-4 font-heading text-3xl font-black leading-[1.2] text-foreground-950 md:text-[42px] md:leading-[1.15]">
              {article.title}
            </h1>
            <p className="mt-4 border-l-[3px] border-primary-500 pl-4 text-[15px] leading-relaxed text-foreground-700 md:text-base">
              {article.summary}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3.5 text-sm text-foreground-600">
              <span className="inline-flex items-center gap-1.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">瓜</span>
                <span className="font-semibold text-foreground-800">编辑部 · {article.host}</span>
              </span>
              <span className="inline-flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true" />{article.viewsText} 围观</span>
              <span className="inline-flex items-center gap-1"><i className="ri-heart-3-line" aria-hidden="true" />{article.likesText}</span>
              <span className="inline-flex items-center gap-1"><i className="ri-chat-3-line" aria-hidden="true" />{article.commentsText} 热议</span>
              <span className="inline-flex items-center gap-1"><i className="ri-time-line" aria-hidden="true" />约 {article.readMin} 分钟</span>
            </div>
          </header>

          <figure className="mt-6 overflow-hidden rounded-2xl border border-background-200">
            <img src={article.image} alt={article.title} className="h-64 w-full object-cover object-top md:h-[420px]" />
            <figcaption className="bg-background-100 px-4 py-2.5 text-xs text-foreground-500">
              图：{article.categoryName}专题配图 · 快手吃瓜瓜料大全
            </figcaption>
          </figure>

          <div className="mt-6 flex flex-col gap-5">
            {article.content.map((p, i) => (
              <p key={i} className="text-[15px] leading-[1.95] text-foreground-800 md:text-base md:leading-[2]">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-accent-200 bg-accent-50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
              <i className="ri-vip-diamond-fill text-accent-600" aria-hidden="true" />
              本期关键信息
            </h2>
            <ul className="mt-3 flex flex-col gap-2.5">
              {article.points.map((pt, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-200 text-[11px] font-bold text-accent-900">
                    {i + 1}
                  </span>
                  {pt}
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-6 rounded-xl bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-500">
            <i className="ri-information-line mr-1" aria-hidden="true" />
            本文为公开信息的时间线与多方说法整理，标注「网传待核实」的内容仅供参考，理性吃瓜，尊重当事人。本站内容不代表当事人立场。
          </p>
        </article>

        <aside className="lg:col-span-4" aria-label="栏目信息">
          <div className="lg:sticky lg:top-24 lg:flex lg:flex-col lg:gap-5">
            <div className="rounded-2xl border border-background-200 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-grid-line text-primary-600" aria-hidden="true" />
                相关栏目
              </h3>
              <div className="mt-4 flex flex-col gap-3">
                {['live', 'circle', 'brand', 'show', 'rule', 'story'].map((cid, i) => {
                  const names = ['直播带货', '主播圈', '品牌争议', '综艺饭圈', '平台新规', '口碑故事'];
                  const icons = ['ri-shopping-bag-3-line', 'ri-user-heart-line', 'ri-flag-2-line', 'ri-vidicon-line', 'ri-file-list-3-line', 'ri-movie-2-line'];
                  const colors = ['bg-primary-100 text-primary-700', 'bg-accent-100 text-accent-900', 'bg-secondary-100 text-secondary-800', 'bg-accent-100 text-secondary-800', 'bg-background-200 text-primary-700', 'bg-primary-100 text-secondary-800'];
                  const active = cid === article.categoryId;
                  return (
                    <a key={cid} href="/#featured" className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${active ? 'border-primary-300 bg-primary-50' : 'border-transparent hover:bg-background-200/60'}`}>
                      <span className={`flex h-8 w-8 items-center justify-center rounded-lg text-base ${colors[i]}`}>
                        <i className={icons[i]} aria-hidden="true" />
                      </span>
                      <span className="flex-1 text-sm font-semibold text-foreground-800">{names[i]}</span>
                      {active && <span className="rounded-full bg-primary-600 px-2 py-0.5 text-[10px] font-bold text-background-50">当前</span>}
                    </a>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-background-200 bg-foreground-950 p-5 text-background-50">
              <h3 className="flex items-center gap-2 text-sm font-bold">
                <i className="ri-thumb-up-line text-accent-400" aria-hidden="true" />
                看瓜提示
              </h3>
              <p className="mt-2 text-[13px] leading-relaxed text-background-200">
                热榜每 30 分钟更新，反映当下最受关注的瓜料讨论趋势。想一次补齐当天的热闹，回到热榜快速浏览即可。
              </p>
              <a href="/#hot" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-4 py-2 text-[13px] font-semibold text-foreground-950 hover:bg-accent-400">
                去今日热榜
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </a>
            </div>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="border-t border-background-200 bg-background-100 py-12 md:py-14">
          <div className="container-page">
            <div className="flex items-end justify-between">
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">相关推荐 · 继续补瓜</h2>
              <a href="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800">
                返回首页<i className="ri-arrow-right-line" aria-hidden="true" />
              </a>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <a key={r.id} href={`/post/${r.id}`} className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-200">
                  <div className="overflow-hidden">
                    <img src={r.image} alt={r.title} loading="lazy" className="h-40 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-semibold text-primary-700">{r.categoryName}</span>
                    <h3 className="mt-2 line-clamp-2 font-heading text-[15px] font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                      {r.title}
                    </h3>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}